import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import Modal from "components/shared/Modal";
import { useCreateProperty } from "./hooks/usePropertyData";
import { Button } from "components/ui/button";
import { Checkbox } from "components/ui/checkbox";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Spinner } from "components/ui/spinner";

const EXTERNAL_ID_PATTERN = /^[A-Za-z0-9_-]+$/;

interface PropertyCreateModalProps {
  show: boolean;
  onHide: () => void;
}

export default function PropertyCreateModal({ show, onHide }: PropertyCreateModalProps) {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [propertyExternalId, setPropertyExternalId] = useState("");
  const [name, setName] = useState("");
  const [physicalKeyExtension, setPhysicalKeyExtension] = useState(false);
  const [externalIdError, setExternalIdError] = useState<string | null>(null);

  const createMutation = useCreateProperty();

  const handleClose = () => {
    setPropertyExternalId("");
    setName("");
    setPhysicalKeyExtension(false);
    setExternalIdError(null);
    onHide();
  };

  const handleCreate = () => {
    const trimmedExternalId = propertyExternalId.trim();
    const trimmedName = name.trim();

    if (!trimmedExternalId || !trimmedName) {
      enqueueSnackbar("Please fill in all required fields", { variant: "warning" });
      return;
    }

    if (!EXTERNAL_ID_PATTERN.test(trimmedExternalId)) {
      setExternalIdError("External ID may only contain letters, numbers, dashes, and underscores.");
      return;
    }

    setExternalIdError(null);
    createMutation.mutate(
      { propertyExternalId: trimmedExternalId, name: trimmedName, physicalKeyExtension },
      {
        onSuccess: () => {
          enqueueSnackbar("Property created successfully", { variant: "success" });
          queryClient.invalidateQueries({ queryKey: ["properties"] });
          handleClose();
          navigate(`/admin/properties/${trimmedExternalId}`);
        },
        onError: (error: any) => {
          if (error?.response?.status === 409) {
            setExternalIdError("A property with this External ID already exists.");
            return;
          }
          const message = error?.response?.data?.message || error?.message || "Failed to create property";
          enqueueSnackbar(message, { variant: "error" });
        },
      }
    );
  };


  return (
    <Modal show={show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>New Property</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="flex flex-col gap-4">
          <div className="grid gap-2">
            <Label htmlFor="property-create-external-id">Property External ID</Label>
            <Input
              aria-invalid={!!externalIdError}
              id="property-create-external-id"
              required
              value={propertyExternalId}
              onChange={(event) => {
                setPropertyExternalId(event.target.value);
                setExternalIdError(null);
              }}
            />
            {externalIdError ? (
              <p className="text-xs text-destructive" role="alert">
                {externalIdError}
              </p>
            ) : null}
          </div>
          <div className="grid gap-2">
            <Label htmlFor="property-create-name">Name</Label>
            <Input
              id="property-create-name"
              required
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              checked={physicalKeyExtension}
              id="property-create-physical-key-extension"
              onCheckedChange={(checked) =>
                setPhysicalKeyExtension(checked === true)
              }
            />
            <Label htmlFor="property-create-physical-key-extension">
              Physical Key Extension
            </Label>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button type="button" variant="outline" onClick={handleClose} disabled={createMutation.isPending}>
          Cancel
        </Button>
        <Button
          type="button"
          onClick={handleCreate}
          disabled={createMutation.isPending || !propertyExternalId.trim() || !name.trim()}
        >
          {createMutation.isPending ? <Spinner className="size-5" /> : "Create"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
